import axios from 'axios';
import { config } from '@/config/config';

interface GetAddressByPlaceIdParams {
  placeId: string;
}

export const getAddressByPlaceId = async (
  params: GetAddressByPlaceIdParams
) => {
  const requestParams = {
    placeId: params.placeId,
    requestFrom: 'multistore',
    restaurantId: config.store.restaurantId,
  };

  const { data } = await axios.post(
    `${config.api.cloudFunctionsUrl}/getAddressByPlaceId`,
    requestParams,
    {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      },
    }
  );

  return {
    addressComponents: data?.results?.result,
    location: data?.results?.result?.geometry?.location,
  };
};

export const reverseGeocode = async (
  lat: number,
  lng: number
) => {
  try {
    const mapTilerKey = 'Kaj7hj4spq6OTB0wtwjG';
    const { data } = await axios.get(`https://api.maptiler.com/geocoding/${lng},${lat}.json?key=${mapTilerKey}`);

    if (data && data.features && data.features.length > 0) {
      const feature = data.features[0];
      const context = feature.context || [];

      let suburb = '';
      let state = '';
      let postcode = '';
      let country = '';

      context.forEach((c: any) => {
        if (c.id.startsWith('municipality') || c.id.startsWith('local_admin') || c.place_type?.includes('municipality') || c.place_type?.includes('suburb')) {
          suburb = c.text;
        }
        if (c.id.startsWith('region') || c.place_type?.includes('region')) {
          state = c.text;
        }
        if (c.id.startsWith('postal_code') || c.place_type?.includes('postcode')) {
          postcode = c.text;
        }
        if (c.id.startsWith('country')) {
          country = c.text;
        }
      });

      const streetNum = feature.address || '';
      const route = feature.text || '';
      
      const addressComponents = [
        {
          long_name: streetNum,
          short_name: streetNum,
          types: ['street_number']
        },
        {
          long_name: route,
          short_name: route,
          types: ['route']
        },
        {
          long_name: suburb,
          short_name: suburb,
          types: ['locality', 'sublocality', 'sublocality_level_1']
        },
        {
          long_name: state,
          short_name: state,
          types: ['administrative_area_level_1']
        },
        {
          long_name: postcode,
          short_name: postcode,
          types: ['postal_code']
        },
        {
          long_name: country,
          short_name: country,
          types: ['country']
        }
      ].filter(c => c.long_name);

      return {
        description: feature.place_name,
        label: feature.place_name,
        placeId: feature.id || `${lat},${lng}`,
        location: { lat, lng },
        gmaps: {
          formatted_address: feature.place_name,
          address_components: addressComponents,
          geometry: { location: { lat, lng } }
        }
      };
    }

    return null;
  } catch (err) {
    console.error('Reverse geocode error:', err);
    return null;
  }
};

export const fetchAddressSuggestions = async (
  input: string
) => {
  const radius = config.addressSearch.radius;
  const types = config.addressSearch.types;
  const finalRestaurantId = config.store.restaurantId;
  const countryIdentifier = config.store.countryIdentifier;
  const storeId = config.store.storeId;
  const location = config.store.location ? {
    lat: String(config.store.location.lat),
    lng: String(config.store.location.lng),
  } : undefined;

  const searchTypes =
    countryIdentifier === 'GB'
      ? [...types, 'postal_code']
      : types;

  const { data } = await axios.post(`${config.api.cloudFunctionsUrl}/addressSearch`, {
    input,
    types: searchTypes,
    country: countryIdentifier || 'GB',
    requestFrom: 'multistore',
    restaurantId: finalRestaurantId,
    ...(storeId && { storeId }),
    ...(location && { location }),
    radius: Number(radius),
  }, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return {
    predictions: data?.results?.predictions || [],
    postcodeData: data?.postcodeData,
  };
};

